import PropTypes from 'prop-types';
import React from 'react';
import styled, { withTheme } from 'styled-components';
import FocusTrap from 'focus-trap-react';

import hooks from '@g4/automation-hooks';
import Portal from '@g4/boneyard/lib/Portal';
import Box from '@g4/boneyard/lib/Box';
import { Media } from '@g4/boneyard/lib/Media';

import Text from '../Boneyard/Text';
import Dismissible from '../Dismissible';
import useBodyScrollLock from '../useBodyScrollLock';

import closeIcon from '../../images/ic-close-blue.svg';
import closeIconGrey from '../../images/ic-close-grey.svg';

function stopBubble(evt) {
  evt.stopPropagation();
}

const ModalTitle = styled(Box)`
  text-align: left;
  ${props => props.theme.query.md} {
    text-align: ${props => props.titleAlign};
  }
`;

const Background = styled(Box)`
  height: 100%;
  width: 100%;
  opacity: 0.9;
`;

const OverlayShim = styled(Box)`
  height: 100%;
`;

const Wrapper = styled.div`
  position: relative;
  box-shadow: 0 5px 20px 0 rgba(96, 125, 139, 0.5),
    0 5px 5px 0 rgba(120, 144, 156, 0.2);
  border-radius: 10px;
  background-color: ${props => props.theme.colors.hob.login.background};
  max-height: 100%;
  max-width: ${props => props.theme.layout.constrain}px;
  display: flex;
  flex-direction: column;
  white-space: normal;
  color: ${props => props.theme.colors.hob.login.color};
  padding: 30px 10px;
  ${props => props.theme.query.md} {
    padding: 30px;
  }
`;

const Overlay = styled(Box)`
  height: 100%;
  width: 100%;
`;

const CloseIcon = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  height: 24px;
  width: 24px;
  ${props => props.theme.query.md} {
    top: 32px;
    right: 32px;
  }
`;

// TODO we need to find a solution for locking body scroll

const Popup = ({
  title,
  titleAlign,
  automationHook,
  className,
  open,
  onRequestClose,
  theme,
  children,
  withDismissible,
  noDismissiblePadding,
  isSunseekerPromotion,
  isSunseekerPromotionCard,
}) => {
  const handleKeyDown = e => {
    if (e.keyCode === 27) {
      onRequestClose();
    }
  };

  const targetElement = useBodyScrollLock(open);
  return (
    <Media>
      {mq => (
        <React.Fragment>
          {mq.lte('sm') && withDismissible ? (
            <Dismissible
              noPadding={noDismissiblePadding}
              active={open}
              onDismiss={onRequestClose}
              direction="up"
              isSunseekerPromotion={isSunseekerPromotion}
              isSunseekerPromotionCard={isSunseekerPromotionCard}
            >
              {children}
            </Dismissible>
          ) : (
            <React.Fragment>
              {open ? (
                <Portal>
                  <div onClick={onRequestClose} ref={targetElement}>
                    <Overlay top={0} left={0} position="fixed" zIndex={999}>
                      <Background
                        position="absolute"
                        top={0}
                        left={0}
                        backgroundColor="rgba(51, 51, 51, 0.8)"
                      />
                      <OverlayShim
                        position="relative"
                        d="flex"
                        justifyContent="center"
                        alignItems="center"
                        p={3}
                      >
                        <FocusTrap
                          active={true}
                          focusTrapOptions={{
                            clickOutsideDeactivates: true,
                          }}
                        >
                          <Wrapper
                            onKeyDown={handleKeyDown}
                            {...hooks.hook(automationHook)}
                            role="status"
                            onClick={stopBubble}
                          >
                            <ModalTitle
                              pb={6}
                              ml={6}
                              mr={6}
                              flex="1"
                              titleAlign={titleAlign}
                            >
                              <Text
                                size={22}
                                weight={500}
                                color={theme.colors.common.peacockBlue}
                              >
                                {title}
                              </Text>
                            </ModalTitle>
                            <CloseIcon onClick={onRequestClose}>
                              <img
                                alt="closeIcon"
                                src={
                                  isSunseekerPromotion ||
                                  isSunseekerPromotionCard
                                    ? closeIconGrey
                                    : closeIcon
                                }
                              />
                            </CloseIcon>

                            <Box overflow="auto">{children}</Box>
                          </Wrapper>
                        </FocusTrap>
                      </OverlayShim>
                    </Overlay>
                  </div>
                </Portal>
              ) : null}
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </Media>
  );
};

Popup.propTypes = {
  title: PropTypes.string,
  titleAlign: PropTypes.oneOf(['left', 'center']),
  /** Associates a specific action onto an element for the benefit of automated clients consuming it */
  automationHook: PropTypes.string,
  /** Class attribute */
  className: PropTypes.string,
  /** Determines if modal is displaying */
  open: PropTypes.bool,
  /** all component children nodes */
  onRequestClose: PropTypes.func,
  /** use dismissible on mobile viewport */
  withDismissible: PropTypes.bool,
  theme: PropTypes.object.isRequired,
  children: PropTypes.node,
  noDismissiblePadding: PropTypes.bool,
  isSunseekerPromotion: PropTypes.bool,
  isSunseekerPromotionCard: PropTypes.bool,
};

Popup.defaultProps = {
  title: '',
  titleAlign: 'center',
  onRequestClose: Function.prototype,
  className: '',
  open: false,
  withDismissible: false,
  noDismissiblePadding: false,
};

export default withTheme(Popup);
